import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { useCreateWaContact, useWaTags } from '@hooks/useWaContacts'
import { WaTag } from '@/types'
import { toast } from 'react-hot-toast'

interface CreateWaContactDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ContactFormData {
  phone: string
  name: string
  notes: string
  tagIds: number[]
}

export function CreateWaContactDialog({ isOpen, onClose, onSuccess }: CreateWaContactDialogProps) {
  const [formData, setFormData] = useState<ContactFormData>({
    phone: '',
    name: '',
    notes: '',
    tagIds: [],
  })

  const { data: tags } = useWaTags()
  const createContact = useCreateWaContact()

  const handleClose = () => {
    setFormData({
      phone: '',
      name: '',
      notes: '',
      tagIds: [],
    })
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.phone.trim()) {
      toast.error('Phone number is required')
      return
    }

    // Clean up phone number (remove spaces, dashes, parentheses but keep +)
    const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '')

    // Basic validation - must have digits, optionally starting with +
    if (!/^\+?\d{7,15}$/.test(cleanPhone)) {
      toast.error('Please enter a valid phone number (e.g., +381 61 1234567)')
      return
    }

    try {
      await createContact.mutateAsync({
        phone: cleanPhone,
        name: formData.name.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        tagIds: formData.tagIds.length > 0 ? formData.tagIds : undefined,
      })

      toast.success('Contact created successfully')
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create contact')
    }
  }

  const toggleTag = (tagId: number) => {
    setFormData((prev) => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((id) => id !== tagId)
        : [...prev.tagIds, tagId],
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Add WhatsApp Contact</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Phone Number */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+381 61 1234567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
            <p className="text-xs text-gray-500">Include country code (e.g., +381 for Serbia, +55 for Brazil)</p>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Contact name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tags</label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag: WaTag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      formData.tagIds.includes(tag.id)
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={
                      formData.tagIds.includes(tag.id)
                        ? undefined
                        : { backgroundColor: tag.color ? `${tag.color}20` : undefined }
                    }
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes about this contact..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={createContact.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createContact.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>{createContact.isPending ? 'Creating...' : 'Add Contact'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
